import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

const VIP_STATUS_OPTIONS = [
  'window_reserved',
  'en_route',
  'artist_ready',
  'handoff_in_progress',
  'in_service',
  'completed',
  'cancelled',
];

function HandoffProtocolContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();

  const [shops, setShops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const [handoffs, setHandoffs] = useState([]);
  const [liveVips, setLiveVips] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vipLoading, setVipLoading] = useState(true);
  const [buddyLoading, setBuddyLoading] = useState(true);
  const [showAddBuddy, setShowAddBuddy] = useState(false);
  const [newBuddy, setNewBuddy] = useState({ artist_id: '', buddy_id: '' });

  useEffect(() => {
    api.getAdminShops().then(setShops).catch(() => setShops([]));
  }, []);

  useEffect(() => {
    if (!selectedShopId) {
      setBranches([]);
      return;
    }
    api.getAdminBranches(parseInt(selectedShopId, 10))
      .then(setBranches)
      .catch(() => setBranches([]));
  }, [selectedShopId]);

  const fetchHandoffs = async () => {
    setLoading(true);
    try {
      const shopId = selectedShopId ? parseInt(selectedShopId, 10) : null;
      const data = await api.getAdminPremiumBookings(shopId, 'handoff_in_progress');
      let filtered = Array.isArray(data) ? data : [];
      if (selectedBranchId) {
        filtered = filtered.filter((h) => String(h.branch_id) === String(selectedBranchId));
      }
      setHandoffs(filtered);
    } catch (err) {
      console.error(err);
      setHandoffs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveVips = async () => {
    setVipLoading(true);
    try {
      const shopId = selectedShopId ? parseInt(selectedShopId, 10) : null;
      const branchId = selectedBranchId ? parseInt(selectedBranchId, 10) : null;
      const data = await api.getAdminActiveVips({ shopId, branchId });
      setLiveVips(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setLiveVips([]);
    } finally {
      setVipLoading(false);
    }
  };

  const fetchBuddies = async () => {
    setBuddyLoading(true);
    try {
      const branchId = selectedBranchId ? parseInt(selectedBranchId, 10) : null;
      const data = await api.getAdminArtistBuddies(branchId || undefined);
      setBuddies(data || []);
    } catch (err) {
      console.error(err);
      setBuddies([]);
    } finally {
      setBuddyLoading(false);
    }
  };

  useEffect(() => {
    fetchHandoffs();
    fetchLiveVips();
    fetchBuddies();
  }, [selectedShopId, selectedBranchId]);

  useEffect(() => {
    if (!selectedBranchId) {
      setArtists([]);
      return;
    }
    api.getAdminArtists({ branchId: parseInt(selectedBranchId, 10) })
      .then(setArtists)
      .catch(() => setArtists([]));
  }, [selectedBranchId]);

  const completeHandoff = async (bookingId) => {
    try {
      await api.updateAdminPremiumBookingStatus(bookingId, 'completed');
      showAlert(t('vip.handoffCompleted', 'Handoff completed'));
      fetchHandoffs();
    } catch (err) {
      showAlert(err.message);
    }
  };

  const updateVipStatus = async (bookingId, status) => {
    try {
      await api.updateAdminPremiumBookingStatus(bookingId, status);
      fetchLiveVips();
      fetchHandoffs();
    } catch (err) {
      showAlert(err.message);
    }
  };

  const addBuddy = async () => {
    if (!newBuddy.artist_id || !newBuddy.buddy_id) {
      showAlert(t('vip.selectBothArtists', 'Please select both artists'));
      return;
    }
    if (!selectedBranchId) {
      showAlert(t('attendance.selectBranch') || 'Select a branch first');
      return;
    }
    if (String(newBuddy.artist_id) === String(newBuddy.buddy_id)) {
      showAlert(t('vip.cannotBuddySelf', 'An artist cannot be their own buddy'));
      return;
    }
    try {
      await api.createAdminArtistBuddy(
        parseInt(newBuddy.artist_id, 10),
        parseInt(newBuddy.buddy_id, 10),
        parseInt(selectedBranchId, 10),
      );
      showAlert(t('vip.buddyAdded', 'Buddy pair added'));
      setShowAddBuddy(false);
      setNewBuddy({ artist_id: '', buddy_id: '' });
      fetchBuddies();
    } catch (err) {
      showAlert(err.message);
    }
  };

  const removeBuddy = (buddyId) => {
    showConfirm(t('vip.confirmDeleteBuddy', 'Remove this buddy pair?'), async () => {
      try {
        await api.deleteAdminArtistBuddy(buddyId);
        fetchBuddies();
      } catch (err) {
        showAlert(err.message);
      }
    });
  };

  const avatarStyle = (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${color}, ${color}88)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.9rem',
    flexShrink: 0,
  });

  const clientName = (h) => h.user_name || h.client_name || h.user?.name || `Client #${h.user_id}`;

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('vip.handoffProtocol', 'Handoff Protocol')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.selectSalon')}</label>
          <select
            value={selectedShopId}
            onChange={(e) => {
              setSelectedShopId(e.target.value);
              setSelectedBranchId('');
            }}
            style={{ ...S.select, width: 'auto', marginBottom: 0, minWidth: 180 }}
          >
            <option value="">{t('admin.allSalons', 'All salons')}</option>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, marginBottom: '0.35rem' }}>{t('vip.allBranches', 'Branch')}</label>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            style={{ ...S.input, maxWidth: '300px', marginBottom: 0 }}
            disabled={!selectedShopId}
          >
            <option value="">{t('vip.allBranches', 'All Branches')}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', alignItems: 'start' }}>

        <div>
          <h2 style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700,
            color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem',
          }}
          >
            {t('vip.activeHandoffs', 'Active Handoffs')}
          </h2>

          {loading ? <Skeleton count={3} height="160px" /> : handoffs.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: T.muted }}>{t('vip.noActiveHandoffs', 'No active handoffs')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {handoffs.map((h) => (
                <div
                  key={h.id}
                  style={{
                    ...S.card,
                    padding: '1.25rem',
                    borderLeft: '4px solid #a855f7',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={avatarStyle('#f59e0b')}>
                      {(clientName(h) || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: T.white, fontSize: '0.9rem' }}>{clientName(h)}</div>
                      <div style={{ fontSize: '0.72rem', color: T.muted }}>
                        {h.service_names || h.service_name}
                        {' '}
                        &middot;
                        {' '}
                        {h.branch_name}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                    padding: '0.85rem',
                    borderRadius: '10px',
                    background: T.bg,
                  }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ ...avatarStyle('#3b82f6'), margin: '0 auto 0.35rem' }}>
                        {(h.artist_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: T.white, fontWeight: 600 }}>{h.artist_name}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <div style={{ width: '40px', height: '2px', background: '#a855f7' }} />
                      <div style={{ color: '#a855f7', fontSize: '1.2rem', lineHeight: 1 }}>&#x279C;</div>
                      <div style={{ width: '40px', height: '2px', background: '#a855f7' }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ ...avatarStyle('#22c55e'), margin: '0 auto 0.35rem' }}>
                        {(h.handoff_artist_name || h.buddy_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: T.white, fontWeight: 600 }}>{h.handoff_artist_name || h.buddy_name || t('vip.unassigned', 'Unassigned')}</div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => completeHandoff(h.id)}
                    style={{
                      padding: '0.6rem 1.2rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#22c55e',
                      color: '#fff',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      width: '100%',
                    }}
                  >
                    {t('vip.confirmHandoffComplete', 'Confirm Handoff Complete')}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
            marginTop: '2rem', marginBottom: '1rem',
          }}
          >
            <h2 style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '1.05rem', fontWeight: 700,
              color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0,
            }}
            >
              {t('admin.activeVipsLive', 'Live VIPs (today)')}
            </h2>
            <button
              type="button"
              onClick={() => fetchLiveVips()}
              style={{ ...S.btnOutline, padding: '0.35rem 0.85rem', fontSize: '0.78rem' }}
            >
              {t('admin.refreshActiveVips', 'Refresh')}
            </button>
          </div>
          {vipLoading ? <Skeleton count={2} height="100px" /> : liveVips.length === 0 ? (
            <p style={{ color: T.muted }}>{t('admin.noActiveVips', 'No active VIPs for this filter.')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {liveVips.map((b) => (
                <div key={b.id} style={{ ...S.card, padding: '1rem' }}>
                  <div style={{ fontWeight: 600, color: T.white }}>{b.user_name}</div>
                  <div style={{ fontSize: '0.78rem', color: T.muted }}>{b.branch_name} · {b.window_start}–{b.window_end}</div>
                  <div style={{ fontSize: '0.75rem', color: T.orange, marginTop: '0.35rem' }}>{b.status}</div>
                  <select
                    value={b.status}
                    onChange={(e) => updateVipStatus(b.id, e.target.value)}
                    style={{ ...S.select, marginTop: '0.5rem', width: '100%' }}
                  >
                    {VIP_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700,
              color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em',
            }}
            >
              {t('vip.buddyConfig', 'Buddy Artist Config')}
            </h2>
            <button
              type="button"
              onClick={() => setShowAddBuddy(!showAddBuddy)}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: T.orange,
                color: '#000',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.78rem',
              }}
            >
              {showAddBuddy ? t('common.cancel', 'Cancel') : t('vip.addBuddy', '+ Add Buddy')}
            </button>
          </div>

          {showAddBuddy && (
            <div style={{ ...S.card, padding: '1.25rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <select
                value={newBuddy.artist_id}
                onChange={(e) => setNewBuddy((p) => ({ ...p, artist_id: e.target.value }))}
                style={S.input}
              >
                <option value="">{t('vip.selectArtist', 'Select Artist')}</option>
                {artists.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <select
                value={newBuddy.buddy_id}
                onChange={(e) => setNewBuddy((p) => ({ ...p, buddy_id: e.target.value }))}
                style={S.input}
              >
                <option value="">{t('vip.selectBuddy', 'Select Buddy Artist')}</option>
                {artists.filter((a) => String(a.id) !== String(newBuddy.artist_id)).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <button type="button" onClick={addBuddy} style={{
                padding: '0.5rem 1.2rem',
                borderRadius: '8px',
                border: 'none',
                background: '#22c55e',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.82rem',
              }}
              >
                {t('vip.saveBuddy', 'Save Buddy Pair')}
              </button>
            </div>
          )}

          {buddyLoading ? <Skeleton count={4} height="64px" /> : buddies.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: T.muted }}>{t('vip.noBuddies', 'No buddy pairs configured')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {buddies.map((buddy) => (
                <div
                  key={buddy.id}
                  style={{
                    ...S.card,
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.85rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <div style={avatarStyle('#3b82f6')}>
                      {(buddy.artist_name || '?')[0].toUpperCase()}
                    </div>
                    <span style={{ color: T.white, fontWeight: 600, fontSize: '0.88rem' }}>{buddy.artist_name}</span>
                  </div>
                  <div style={{ color: T.muted, fontSize: '1.1rem' }}>&#x279C;</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <div style={avatarStyle('#22c55e')}>
                      {(buddy.buddy_name || '?')[0].toUpperCase()}
                    </div>
                    <span style={{ color: T.white, fontWeight: 600, fontSize: '0.88rem' }}>{buddy.buddy_name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeBuddy(buddy.id)}
                    style={{
                      padding: '0.35rem 0.65rem',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#ef444422',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                    }}
                  >
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default function AdminHandoffProtocol() {
  return (
    <ProtectedRoute requireSuperAdmin>
      <HandoffProtocolContent />
    </ProtectedRoute>
  );
}
